/**
 * PlayerChipsDisplay for Poker Reborn
 *
 * Player-specific chip display wrapper for SeatView.
 * Composes ChipsDisplay with player-specific functionality:
 * - Winner celebration (flash + pulse)
 * - Responsive sizing via BBS (discrete mode)
 *
 * @see docs/screens/room/CHIPS_DISPLAY.md - Full documentation
 * @see docs/screens/room/SEAT.md - Parent component (SeatView)
 */

import { Container } from 'pixi.js';
import { ChipsDisplay } from '@/components/common/ChipsDisplay';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// BBS BREAKPOINTS
// =============================================================================

/**
 * Font size breakpoints for player chips display.
 * Uses discrete mode (jumps at breakpoints).
 */
const FONT_SIZE_BREAKPOINTS = { 375: 12, 720: 14, 1440: 16, 1920: 16 };

/** Player chip display colors */
const COLORS = {
  normal: 0xffd700,      // Gold
  increase: 0x4caf50,    // Green (win)
  decrease: 0xf44336,    // Red (bet/loss)
};

// =============================================================================
// PLAYER CHIPS DISPLAY
// =============================================================================

export class PlayerChipsDisplay extends Container {
  private chipsDisplay: ChipsDisplay;
  private currentScreenWidth = 1920;

  constructor() {
    super();

    this.chipsDisplay = new ChipsDisplay({
      color: COLORS.normal,
      increaseColor: COLORS.increase,
      decreaseColor: COLORS.decrease,
      fontSize: getScaledValue(this.currentScreenWidth, FONT_SIZE_BREAKPOINTS, { discrete: true }),
      prefix: '$',
    });

    this.addChild(this.chipsDisplay);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Set the chip amount to display
   * @param amount - The chip amount
   * @param animate - Whether to animate the change (default: true)
   */
  setChips(amount: number, animate: boolean = true): void {
    this.chipsDisplay.setValue(amount, animate);
  }

  /**
   * Set winner state - triggers green flash and pulse
   * Call this before setChips() on hand end for winners
   */
  setWinner(isWinner: boolean): void {
    if (isWinner) {
      this.chipsDisplay.flash(COLORS.increase);
      this.chipsDisplay.pulse();
    }
  }

  /**
   * Get current displayed chip amount
   */
  getChips(): number {
    return this.chipsDisplay.getValue();
  }

  /**
   * Resize for different screen widths
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;

    this.currentScreenWidth = screenWidth;
    const fontSize = getScaledValue(screenWidth, FONT_SIZE_BREAKPOINTS, { discrete: true });
    this.chipsDisplay.setFontSize(fontSize);
  }

  /**
   * Get the underlying ChipsDisplay for advanced usage
   */
  getChipsDisplay(): ChipsDisplay {
    return this.chipsDisplay;
  }

  /**
   * Clean up on destroy
   */
  override destroy(): void {
    this.chipsDisplay.destroy();
    super.destroy();
  }
}
